import type { Country, GetBillingOverviewResponse } from '@acs/shared';

/**
 * Display arithmetic for the plan / minute-package screens (doc 57). Pure, so
 * it is tested without React.
 *
 * Nothing here decides what anyone pays. Before the owner has a Stripe
 * customer (i.e. before the subscription), no tax quote exists, so the
 * screen shows an ESTIMATE from the country's standard VAT rate and labels
 * it as one. Once a customer exists, the server quote replaces the estimate,
 * and Checkout / the invoice compute the real tax either way.
 */

/** Standard VAT rates, for labelled estimates only. A country missing here
 *  shows net prices with "+ VAT" and no gross estimate. */
export const ESTIMATE_VAT_PERCENT: Partial<Record<Country, number>> = { PL: 23, DE: 19, FR: 20 };

export type PackageView = GetBillingOverviewResponse['packages'][number];

/** Net → estimated gross, rounded per line like an invoice line. */
export function estimateGross(netMinor: number, vatPercent: number): number {
  return netMinor + Math.round((netMinor * vatPercent) / 100);
}

export function packageMinutes(p: Pick<PackageView, 'seconds'>): number {
  return Math.floor(p.seconds / 60);
}

/** Minutes per call the "≈ N calls" estimates assume (design v4 footnote). */
export const MINUTES_PER_CALL = 3;

/**
 * Rough number of calls a minute amount covers, rounded to the nearest 5 so it
 * reads as the estimate it is (100 → 35, 300 → 100, 1000 → 335). Shown with
 * an asterisk and the assumption spelled out under the list.
 */
export function estimatedCalls(minutes: number): number {
  return Math.round(minutes / MINUTES_PER_CALL / 5) * 5;
}

/**
 * The package with the lowest net price per minute, or null when fewer than
 * two packages have a price — "lowest" means nothing with one candidate. It is
 * the only badge the screen shows: a fact, not a popularity claim.
 */
export function cheapestPerMinute(packages: readonly PackageView[]): string | null {
  const priced = packages.filter((p) => p.unitAmountMinor !== null && p.seconds > 0);
  if (priced.length < 2) return null;
  let best = priced[0]!;
  for (const p of priced) if (p.unitAmountMinor! / p.seconds < best.unitAmountMinor! / best.seconds) best = p;
  return best.packageId;
}

/** Per-minute price in major units, fixed to 3 decimals so 0.79 and 0.698 line up. */
export function perMinuteText(p: Pick<PackageView, 'unitAmountMinor' | 'seconds' | 'currency'>, tag: string): string | null {
  if (p.unitAmountMinor === null || p.seconds <= 0) return null;
  return new Intl.NumberFormat(tag, { style: 'currency', currency: p.currency, minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(
    p.unitAmountMinor / 100 / packageMinutes(p),
  );
}

/**
 * The package the owner picked, remembered for THIS business across the trip
 * to Stripe Checkout and back. Session-scoped and per business, so it never
 * follows the owner into another business; storage may be unavailable
 * (private mode), in which case the default simply applies.
 */
const selectionKey = (businessId: string) => `tc24.minutePackage.${businessId}`;

export function readSelectedPackage(businessId: string): string | null {
  try {
    return window.sessionStorage.getItem(selectionKey(businessId));
  } catch {
    return null;
  }
}

export function writeSelectedPackage(businessId: string, packageId: string): void {
  try {
    window.sessionStorage.setItem(selectionKey(businessId), packageId);
  } catch {
    /* storage unavailable — the selection lives in component state only */
  }
}

/** A remembered choice if it is still on sale, else the smallest priced package. */
export function initialPackage(packages: readonly PackageView[], remembered: string | null): string | null {
  if (remembered && packages.some((p) => p.packageId === remembered && p.unitAmountMinor !== null)) return remembered;
  const priced = packages.filter((p) => p.unitAmountMinor !== null);
  const pool = priced.length ? priced : packages;
  return [...pool].sort((a, b) => a.seconds - b.seconds)[0]?.packageId ?? null;
}

/** Stored marker for an explicit "no additional package" choice. */
export const NO_PACKAGE = 'none';

/**
 * Initial choice when packages are OPTIONAL (doc 59 N07: a subscription with
 * included minutes needs no add-on). Nothing is preselected unless the owner
 * picked a still-priced package earlier in this session.
 */
export function initialOptionalPackage(packages: readonly PackageView[], remembered: string | null): string | null {
  if (!remembered || remembered === NO_PACKAGE) return null;
  return packages.some((p) => p.packageId === remembered && p.unitAmountMinor !== null) ? remembered : null;
}
